// /////////////////////////////////////////////////////////////
// [ファイル] useAssistantFeedback.ts
// [概要] AI 回答への評価 (👍 良い / 👎 悪い + 理由) を Firestore
//        (HozenallCloud_Assistant_Feedback) に記録・購読するフック。
//        doc ID = "<sessionId>__<messageId>" で 1 回答 1 評価 (再評価は上書き)。
//        評価は本人のセッションの回答にしか付かないため、共有の考慮は不要。
//        蓄積した評価は「事例 → 規則候補 → 検証ケース」の流れで
//        システムプロンプトの見直しと回答精度の検証に使う。
// /////////////////////////////////////////////////////////////
import { useCallback, useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { useAuth } from '../firebase/AuthProvider';
import type { AssistantMessage } from './useAssistantSessions';

const COLLECTION = 'HozenallCloud_Assistant_Feedback';

export type FeedbackRating = 'good' | 'bad';

// 👎 の理由種別。id は Firestore に保存する値 (集計キー) なので変更しない
export const FEEDBACK_CATEGORIES = [
  { id: 'wrong_target', label: '対象の取り違え (別のファイル・別の行を扱った)' },
  { id: 'unapproved_action', label: '独断での実行 (確認・承認なしに書き込んだ)' },
  { id: 'misread', label: '事実誤認・読み違え (内容や状態を誤って断定した)' },
  { id: 'misunderstood', label: '指示の読み違え (依頼と違うことをした)' },
  { id: 'report_format', label: '報告・書式の問題 (実行内容の省略、分かりにくい)' },
  // 回答の誤りではなく、システムに機能が無いために依頼を果たせなかった (ツール追加の要望として集計する)
  { id: 'missing_feature', label: '機能不足 (やりたいことに対応する機能が無い)' },
  { id: 'other', label: 'その他' },
] as const;

export interface AssistantFeedback {
  messageId: string;
  rating: FeedbackRating;
  category?: string;
  comment?: string;
}

export interface SetFeedbackInput {
  message: AssistantMessage; // id 必須 (無い回答は評価できない)
  rating: FeedbackRating;
  category?: string; // 👎 の理由種別 (FEEDBACK_CATEGORIES の id)
  comment?: string; // 任意の補足
  requestText: string; // その回答の直前の依頼文 (事例として読み返すため)
  mode: string; // chat / report / accounting
  groupId: string; // '' = 共通
  agentType: string; // gws / accounting
}

// 回答本文は事例の要約として先頭だけ残す (本文はセッション側に残っている)
const REPLY_EXCERPT_CHARS = 600;
const REQUEST_EXCERPT_CHARS = 1000;

function feedbackDocId(sessionId: string, messageId: string): string {
  return `${sessionId}__${messageId}`;
}

/** 現在セッションの評価を購読し、記録・取り消しの関数を返す */
export function useAssistantFeedback(sessionId: string | null) {
  const { user } = useAuth();
  const email = user?.email ?? null;
  const [feedbackByMessage, setFeedbackByMessage] = useState<Map<string, AssistantFeedback>>(
    () => new Map()
  );

  useEffect(() => {
    if (!sessionId || !email) {
      setFeedbackByMessage(new Map());
      return;
    }
    // 等価条件のみ (複合インデックス不要)。ルールの user_id 一致も満たす
    const q = query(
      collection(db, COLLECTION),
      where('user_id', '==', email),
      where('session_id', '==', sessionId)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = new Map<string, AssistantFeedback>();
        snap.docs.forEach((d) => {
          const data = d.data();
          const messageId = data.message_id as string;
          if (!messageId) return;
          next.set(messageId, {
            messageId,
            rating: data.rating as FeedbackRating,
            category: (data.category as string) || undefined,
            comment: (data.comment as string) || undefined,
          });
        });
        setFeedbackByMessage(next);
      },
      (err) => {
        // 購読失敗 (ルール未反映など) でも会話自体は妨げない
        console.error('回答評価の購読に失敗しました', err);
      }
    );
    return () => unsub();
  }, [sessionId, email]);

  /** 評価を記録する (既存があれば上書き。👍 へ変えた時は理由を消す) */
  const setFeedback = useCallback(
    async (input: SetFeedbackInput) => {
      const messageId = input.message.id;
      if (!sessionId || !email || !messageId) {
        throw new Error('評価対象の回答を特定できません');
      }
      const existing = feedbackByMessage.get(messageId);
      await setDoc(
        doc(db, COLLECTION, feedbackDocId(sessionId, messageId)),
        {
          user_id: email,
          session_id: sessionId,
          message_id: messageId,
          rating: input.rating,
          category: input.rating === 'bad' ? input.category ?? '' : '',
          comment: input.rating === 'bad' ? input.comment ?? '' : '',
          request_text: input.requestText.slice(0, REQUEST_EXCERPT_CHARS),
          reply_excerpt: input.message.text.slice(0, REPLY_EXCERPT_CHARS),
          tool_names: input.message.toolNames ?? [],
          mode: input.mode,
          group_id: input.groupId,
          agent_type: input.agentType,
          updated_at: serverTimestamp(),
          ...(existing ? {} : { created_at: serverTimestamp() }),
        },
        { merge: true }
      );
    },
    [sessionId, email, feedbackByMessage]
  );

  /** 評価を取り消す */
  const clearFeedback = useCallback(
    async (messageId: string) => {
      if (!sessionId) return;
      await deleteDoc(doc(db, COLLECTION, feedbackDocId(sessionId, messageId)));
    },
    [sessionId]
  );

  return { feedbackByMessage, setFeedback, clearFeedback };
}
